import type { ChartData, ChartOptions, ChartTypeRegistry } from "chart.js";
import type {
  LeadingSourcesData as LeadingSourcesDataType,
  LeadingSourcesTableData,
} from "charts/visualisations/LeadingSources/types";
import type { CSSProperties } from "vue";

import type { MediumName } from "shared/helpers/media";
import type { StreamRequestFilters } from "shared/helpers/StreamFilters";
import { type AIVisibilityProjectSource } from "shared/modules/AIVisibility/types";
import type {
  ChartAxisInterval,
  ChartExportOption,
  ChartSettingWithAnalyticsIntegrationTarget,
  ChartVariant,
  InsightsMetric,
  OutreachActivityChartFilters,
  SovMetricType,
} from "shared/modules/Charts/types";
import type {
  ConvertKeysToSnakeCase,
  Extends,
  Nullable,
  Stream,
} from "shared/types/index";
import type { MentionType, ShareTypePlatform } from "shared/types/mentions";

export type VisualisationType =
  | "chart"
  | "map"
  | "table"
  | "print_sources"
  | "online_sources"
  | "tv_programs"
  | "radio_programs"
  | "print_journalists"
  | "online_journalists";

export type Layout = "inline" | "column";

export type IndicatorStyle = "line" | "square" | "circle";

export interface GeographicSpreadChartOptions {
  geographic_spread_scope: string;
}

export interface VolumeOfCoverageByPositionChartOptions {
  medium: Extends<MediumName, "Online" | "Print">;
}

export interface WordCloudChartOptions {
  entity_type:
    | "person"
    | "other"
    | "location"
    | "organization"
    | "organisation"
    | "event"
    | "date"
    | "price"
    | "address";
}

export interface AIVisibilityOptions {
  entity_filters?: {
    brands?: string[];
    products?: string[];
  };
  limit: number;
  sources?: AIVisibilityProjectSource[];
}

export interface ChartLibraryRequestChartOptions
  extends
    ChartOptions<keyof ChartTypeRegistry>,
    Partial<GeographicSpreadChartOptions>,
    Partial<VolumeOfCoverageByPositionChartOptions>,
    Partial<WordCloudChartOptions>,
    Partial<AIVisibilityOptions> {
  analytics_integration_target?: ChartSettingWithAnalyticsIntegrationTarget;
  anz_only?: boolean;
  beta?: boolean;
  collapse_syndicated?: boolean;
  colors?: string[];
  custom_colours?: boolean;
  cutout?: number;
  exclude_llm?: boolean;
  hide_legend?: boolean;
  include_count_in_legend?: boolean;
  show_contacts_only?: boolean;
  show_title?: boolean;
  size?: number;
  stream_ids?: number[];
  visualisation_type?: Extends<
    VisualisationType,
    | "chart"
    | "map"
    | "table"
    | "print_sources"
    | "online_sources"
    | "print_journalists"
    | "online_journalists"
    | "tv_programs"
    | "radio_programs"
  >;
}

export interface KeyQueryParams {
  boolean: string;
  boolean_query?: string;
  keywords: string;
  label: string;
  query_style: "keywords" | "boolean";
  stream_id: number;
}

export type OutreachActivityChartFiltersRequest =
  ConvertKeysToSnakeCase<OutreachActivityChartFilters>;

export interface ChartLibraryRequestParams {
  advanced_filters?: Nullable<StreamRequestFilters>;
  after: number;
  authors?: string;
  before: number;
  chart_type: string;
  chart_variant?: string;
  export_options?: ChartExportOption[];
  filter_set_id?: number;
  insights_metrics?: InsightsMetric[];
  instant_insights_report_id?: number | string;
  interval?: ChartAxisInterval;
  is_public_insights?: boolean;
  linked_stream_ids?: Record<number | string, number>;
  media?: MediumName[];
  media_release_id?: number;
  media_release_ids?: number[];
  metric_type?: SovMetricType;
  options: ChartLibraryRequestChartOptions;
  organisation_social_integration_ids?: string[];
  organisation_team_id?: number;
  outreach_activity_filters?: OutreachActivityChartFiltersRequest;
  outreach_activity_ids?: number[];
  post_ids?: number[];
  queries?: KeyQueryParams[];
  size?: number;
  sort_by?: string;
  time_zone: string;
  types?: MentionType[];
}

export interface ChartLibraryLegendOption {
  color?: string;
  indicatorStyle: IndicatorStyle;
  label: string;
  rightLabel: string;
}

export interface ChartLibaryLegend {
  items: ChartLibraryLegendOption[];
  layout: Layout;
  legendColumnStyle: {};
  legendItemStyle: {};
  legendLimit: Nullable<number>;
  maxColumnsPerRow: Nullable<number | string>;
  maxRowsPerColumn: Nullable<number | string>;
}

export interface AdvertisingValueEquivalencyProps {
  currencyCode: string;
  layout: Layout;
  locale: string;
}

export interface CoverageVolumeAndAnalyticsProps {
  rangeLabel: string;
}

export interface CoverageVolumeTimelineProps {
  clustered: Nullable<boolean>;
  text: string;
  title: string;
}

export interface DomainAuthorityProps {
  ranges: {
    from: number;
    key: string;
    to: number;
  }[];
}

export interface EmojiCountProps {
  selectionEnabled: boolean;
}

export interface LeadingSourcesProps {
  total: number;
  truncateLabelsAt?: number;
}

export interface SentimentTimelineProps {
  legendLayout: Layout;
}

export interface ShareOfVoiceProps {
  size: number;
  streams: Stream[];
  truncateLabels?: boolean;
  visualisationType: VisualisationType;
}

export interface ShareOfVoiceTimelineProps {
  includeCountInLegend?: boolean;
}

export interface VolumeOfCoverageByPositionProps {
  medium: MediumName;
}

export interface WordCloudWord {
  value: number;
  weight: number;
  word: string;
}
export interface WordCloudProps {
  wordColor: string;
  words: WordCloudWord[] | WordCloudWord[][];
}

export interface OutreachTopEngagedContactsProps {
  media_release?: {
    id: number;
    name: string;
  };
}

export interface GeographicSpreadLocation {
  color: string;
  latitude: number;
  location: string;
  location_id: number;
  longitude: number;
  total: number;
}

export interface GeographicSpreadData {
  chart?: {
    datasets: ChartData;
    total?: number;
    type?: string;
  };
  map?: {
    datasets: GeographicSpreadLocation[];
  };
}

export interface ReactScoreBreakdownData {
  datasets: {
    range: {
      max: number;
      min: number;
    }[];
  }[];
}

export interface CoverageVolumeAndReferralTrafficData {
  referred_urls_cache?: string | undefined;
}

export interface BroadcastTimeByMediaTypeBucket {
  color: string;
  label: MediumName;
  referred_urls_cache: Nullable<string> | undefined;
  value: number;
}

export type BroadcastTimeByMediaTypeData = BroadcastTimeByMediaTypeBucket[];

export type TopFacebookPageRow = {
  followers_count: number;
  id: number;
  name: string;
  url: string;
  username: string;
  verified: boolean;
};

export type KeyInfluencersRow = {
  count: number;
  id: number;
  logo: Nullable<string>;
  name: string;
  type:
    | "tweet"
    | "instagram_post"
    | "bluesky_post"
    | "youtube_video"
    | "tiktok_video"
    | "reddit_post"
    | "linkedin_post";
  username: string;
  verified: boolean;
  youtube_id?: string;
};

export interface ChartLibraryTableDataColumn {
  heading: string;
  key?: string;
  rows: string[] | TopFacebookPageRow[] | KeyInfluencersRow[];
  style?: CSSProperties;
}
export interface ChartLibraryTableData {
  columns: ChartLibraryTableDataColumn[];
}

export type LeadingSourcesData = LeadingSourcesDataType;

export interface LeadingSourcesPropsData extends ChartLibraryData {
  data: LeadingSourcesData;
}
export interface LeadingSourcesTablePropsData extends ChartLibraryData {
  data: LeadingSourcesTableData;
}

export interface SentimentBreakdownData {
  fields?: string[];
}

export interface SentimentBenchmarkData {
  datasets: {
    stream_ids: number[];
    totals: number[];
  }[];
}

export interface SentimentTimelineData {
  datasets: {
    field: string;
  }[];
}

export enum ChartLibraryTagId {
  Earned = "earned",
  LLM = "llm",
  Social = "social",
}

type CoverageVolumeAndStockPriceAxisId = "stock_price" | "volume";

export interface CoverageVolumeAndStockPriceData {
  datasets: {
    stream_id?: Nullable<number>;
    tag?: string;
    tag_id?: ChartLibraryTagId;
    yAxisID?: CoverageVolumeAndStockPriceAxisId;
  }[];
}

export interface OutreachCoverageTimelineData {
  datasets: {
    streamId?: Nullable<number>;
    tag?: string;
    tag_id?: ChartLibraryTagId;
  }[];
}

export interface MentionVolumeData {
  analytics_error?: boolean;
  datasets: {
    data: number[];
  }[];
  scales: {
    volume: {
      display: boolean;
    };
    x: {
      grid: {
        color: string;
        display: boolean;
        drawTicks: boolean;
      };
      ticks: {
        display: boolean;
      };
    };
  };
}

export interface TopPlatformPost {
  author_avatar_image?: string;
  body: string;
  comment_count: string;
  follower_count: string;
  has_assets: boolean;
  id: number;
  index: number;
  like_count: string;
  share_count: string;
  url: string;
  username: string;
  verified: boolean;
}

export interface TopPlatformPostsData {
  platform: string;
  top_platform_posts: TopPlatformPost[];
}

export interface ShareOfVoiceData {
  datasets: {
    stream_ids?: Nullable<number>[];
  }[];
}

export interface ShareOfVoiceTimelineData {
  analytics_error?: boolean;
  datasets: {
    label: string;
    stream_id: number;
    tag?: string;
    tag_id?: ChartLibraryTagId;
  }[];
}

export interface KeyMessagesData {
  datasets: {
    streamId: number;
  }[];
}

export interface MentionBenchmarkTimelineDataPoint {
  date: string;
  x: string;
  y: number;
}

export interface MentionBenchmarkTimelineData {
  datasets: {
    data: MentionBenchmarkTimelineDataPoint[];
    label: string;
  }[];
}

export interface TotalSocialSharesData {
  datasets: {
    platform: ShareTypePlatform;
  }[];
  platforms?: ShareTypePlatform[];
}

export interface AIVisibilityTopAuthorsRow {
  answers: number;
  author: string;
  authors: string[];
  index: number;
  influencer_ids: number[];
  share: number;
  sources: number;
}

export interface AIVisibilityTopOutletsRow {
  answers: number;
  outlet_id: number;
  outlet_name: string;
  share: number;
  sources: number;
  url: string;
}
export interface AIVisibilityTopDomainsRow {
  answers: number;
  domain: string;
  share: number;
}

export interface AIVisibilityBenchmarkRow extends Partial<
  Record<AIVisibilityProjectSource, number>
> {
  entity: string;
}

export interface AIVisibilityTableData<
  TRow =
    | AIVisibilityTopAuthorsRow
    | AIVisibilityTopDomainsRow
    | AIVisibilityTopOutletsRow
    | AIVisibilityBenchmarkRow,
> {
  columns: { key: string; label: string }[];
  rows: TRow[];
  total_rows: number;
}

export type AIVisibilitySentimentDataField =
  | "positive"
  | "trending_positive"
  | "balanced"
  | "trending_negative"
  | "negative";

export interface AIVisibilitySentimentData extends ChartData<"pie"> {
  fields: AIVisibilitySentimentDataField[];
  labels: string[];
}

export type ChartLibraryData = Partial<AIVisibilitySentimentData> &
  Partial<AIVisibilityTableData> &
  Partial<ChartData> &
  Partial<ChartLibraryTableData> &
  Partial<CoverageVolumeAndReferralTrafficData> &
  Partial<CoverageVolumeAndStockPriceData> &
  Partial<GeographicSpreadData> &
  Partial<KeyMessagesData> &
  Partial<LeadingSourcesData> &
  Partial<LeadingSourcesTableData> &
  Partial<MentionBenchmarkTimelineData> &
  Partial<MentionVolumeData> &
  Partial<OutreachCoverageTimelineData> &
  Partial<ReactScoreBreakdownData> &
  Partial<SentimentBenchmarkData> &
  Partial<SentimentBreakdownData> &
  Partial<SentimentTimelineData> &
  Partial<ShareOfVoiceData> &
  Partial<ShareOfVoiceTimelineData> &
  Partial<TopPlatformPostsData> &
  Partial<TotalSocialSharesData>;

export interface OutreachPerformanceChartLibraryData extends ChartLibraryData {
  media_release_names?: string[];
  media_releases_count?: number;
}

interface AIVisibilityDataTablePropsData extends ChartLibraryData {
  data: AIVisibilityTableData;
}

interface AIVisibilitySentimentPropsData extends ChartLibraryData {
  data: AIVisibilitySentimentData;
}

export interface ChartLibraryLegendItem {
  color: string;
  indicatorStyle?: IndicatorStyle;
  label: string;
  platform?: string;
  rightLabel?: string;
  source_id?: AIVisibilityProjectSource;
  stream_id?: Nullable<number>;
  tag?: string;
  tag_color?: string;
  tag_id?: ChartLibraryTagId;
}

export type ChartLibraryLegend = ChartLibraryLegendItem[];

export interface BaseChartLibraryOptions {
  after: number;
  animation?: boolean;
  before: number;
  bookmark_stream_ids: number[];
  chart_type: string;
  chart_variant?: ChartVariant;
  include_bookmark_syndication: boolean;
  linked_stream_ids: Record<number | string, number>;
  media: MediumName[];
  media_release_ids: number[];
  options: Partial<ChartLibraryOptions>;
  queries: KeyQueryParams[];
  size: number;
  time_zone: string;
  types: MentionType[];
}

export interface ChartLibraryOptions extends Partial<BaseChartLibraryOptions> {
  collapse_syndicated?: boolean;
  colors?: string[];
  custom_colours?: boolean;
  include_count_in_legend?: boolean;
  includeCountInLegend?: boolean;
  medium?: MediumName;
  platform?: "facebook" | "twitter";
  show_title?: boolean;
  stream_ids?: number[];
  visualisation_type?: VisualisationType;
}

export interface BaseChartLibraryProps<
  T = ChartLibraryData | BroadcastTimeByMediaTypeData,
> {
  chartType: string;
  data: T;
  legend: ChartLibraryLegend;
  options: Partial<ChartLibraryOptions>;
  stream: Stream;
  style: CSSProperties;
}

export interface ChartWithLegendLayoutProps {
  legendPosition?: "top" | "bottom" | "left" | "right";
  showTotal?: boolean;
}

export interface ChartWithDataTableProps {
  columnStyle?: CSSProperties;
  headingStyle?: CSSProperties;
  rowBorderDisabled?: boolean;
  rowHoverStyle?: CSSProperties;
}

export interface ChartLibraryProps
  extends
    Partial<AdvertisingValueEquivalencyProps>,
    Partial<BaseChartLibraryProps>,
    Partial<ChartWithLegendLayoutProps>,
    Partial<ChartWithDataTableProps>,
    Partial<CoverageVolumeAndAnalyticsProps>,
    Partial<CoverageVolumeTimelineProps>,
    Partial<DomainAuthorityProps>,
    Partial<EmojiCountProps>,
    Partial<LeadingSourcesProps>,
    Partial<OutreachTopEngagedContactsProps>,
    Partial<SentimentTimelineProps>,
    Partial<ShareOfVoiceProps>,
    Partial<ShareOfVoiceTimelineProps>,
    Partial<VolumeOfCoverageByPositionProps>,
    Partial<WordCloudProps> {}

export interface OutreachPerformanceChartLibraryProps extends ChartLibraryProps {
  data: OutreachPerformanceChartLibraryData;
}

export interface AIVisibilityDataTableProps extends ChartLibraryProps {
  data: AIVisibilityDataTablePropsData;
}

export interface AIVisibilitySentimentProps extends ChartLibraryProps {
  data: AIVisibilitySentimentPropsData;
}

export interface LeadingSourcesChartLibraryProps extends ChartLibraryProps {
  data: LeadingSourcesPropsData;
}

export interface LeadingSourcesTableChartLibraryProps extends ChartLibraryProps {
  data: LeadingSourcesTablePropsData;
}
